/**
 * Remove GFM markdown tables from a markdown string.
 *
 * askRichard answers often include both a chart and a markdown table of the same numbers
 * (the system prompt asks for both). When a chart is present the inline markdown table is
 * redundant with the evidence drawer's Data tab and causes a "raw table then chart" flash,
 * so we strip it from the prose while leaving the rest of the answer intact.
 *
 * A GFM table is a header row containing a pipe, immediately followed by a delimiter row
 * (only pipes, dashes, colons and spaces, with at least one dash and one pipe), then zero or
 * more contiguous body rows containing a pipe. Only that block is removed; surrounding prose,
 * lists, headings and code fences are preserved.
 */
export function stripMarkdownTables(markdown: string): string {
  if (!markdown || markdown.indexOf('|') === -1) {
    return markdown;
  }

  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const isDelimiterRow = (line: string): boolean => {
    const t = line.trim();
    return t.length > 0 && /^[|\s:-]+$/.test(t) && t.includes('-') && t.includes('|');
  };
  const looksLikeRow = (line: string): boolean => line.includes('|') && line.trim() !== '';
  // A body row must be clearly table-shaped (leading pipe or two-plus pipes) so a following prose
  // line with a single stray pipe, or a code-fence line, is not swallowed into the table.
  const isTableBodyRow = (line: string): boolean => {
    const t = line.trim();
    if (t === '' || !t.includes('|')) {
      return false;
    }
    const pipeCount = (t.match(/\|/g) ?? []).length;
    return t.startsWith('|') || pipeCount >= 2;
  };

  const out: string[] = [];
  let removed = false;
  let i = 0;
  while (i < lines.length) {
    if (i + 1 < lines.length && looksLikeRow(lines[i]) && isDelimiterRow(lines[i + 1])) {
      // header + delimiter, then consume contiguous, clearly table-shaped body rows
      i += 2;
      while (i < lines.length && isTableBodyRow(lines[i])) {
        i += 1;
      }
      removed = true;
      continue;
    }
    out.push(lines[i]);
    i += 1;
  }

  if (!removed) {
    return markdown;
  }
  // Collapse the blank-line gaps left behind so the prose reads cleanly.
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}
